import { useState } from "react"
import { useNpsDashboard, useNpsCampaigns, useNpsSurveys, useActionPlans, useSendNpsCampaign, useCreateNpsCampaign } from "@/hooks/useCustomerSuccess"
import type { NpsCampaign, NpsSurvey, ActionPlan } from "@/types/customerSuccess"
import { Card } from "@/components/ui/Card"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import { Badge } from "@/components/ui/Badge"
import { formatDate } from "@/lib/utils"
import {
  HeartHandshake, BarChart3, Send, AlertTriangle, Building2, Users,
  Loader2, Plus, X, TrendingUp, TrendingDown, Minus,
} from "lucide-react"

// ==========================================
// Status configs
// ==========================================

const CAMPAIGN_STATUS: Record<string, { label: string; color: string }> = {
  draft: { label: "Rascunho", color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300" },
  scheduled: { label: "Agendada", color: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300" },
  sending: { label: "Enviando", color: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300" },
  completed: { label: "Concluida", color: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" },
  cancelled: { label: "Cancelada", color: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300" },
}

const SEVERITY_CONFIG: Record<string, { label: string; color: string }> = {
  low: { label: "Baixa", color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300" },
  medium: { label: "Media", color: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300" },
  high: { label: "Alta", color: "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300" },
  critical: { label: "Critica", color: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300" },
}

const AP_STATUS: Record<string, { label: string; color: string }> = {
  open: { label: "Aberto", color: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300" },
  in_progress: { label: "Em Andamento", color: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300" },
  closed: { label: "Fechado", color: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" },
}

function npsColor(score: number): string {
  if (score >= 50) return "text-emerald-500"
  if (score >= 0) return "text-amber-500"
  return "text-red-500"
}

function npsBgColor(score: number): string {
  if (score >= 50) return "bg-emerald-500"
  if (score >= 0) return "bg-amber-500"
  return "bg-red-500"
}

function npsLabel(score: number): string {
  if (score >= 75) return "Excelente"
  if (score >= 50) return "Muito Bom"
  if (score >= 0) return "Razoavel"
  return "Critico"
}

function scoreColor(score: number | null | undefined): string {
  if (score == null) return ""
  if (score >= 9) return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300"
  if (score >= 7) return "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300"
  return "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300"
}

function scoreCategory(score: number | null | undefined): string {
  if (score == null) return ""
  if (score >= 9) return "Promotor"
  if (score >= 7) return "Neutro"
  return "Detrator"
}

// ==========================================
// NPS Gauge Component
// ==========================================

function NpsGauge({ score, hasData }: { score: number; hasData: boolean }) {
  // Semi-circle gauge: -100 to 100
  const normalized = Math.max(-100, Math.min(100, score))
  const rotation = hasData ? ((normalized + 100) / 200) * 180 : 0 // 0 to 180 degrees

  return (
    <div className="relative w-40 h-24 mx-auto">
      {/* Background arc */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="w-40 h-40 rounded-full border-[12px] border-gray-200 dark:border-gray-700"
          style={{ clipPath: "inset(50% 0 0 0)" }}
        />
      </div>
      {/* Colored arc */}
      {hasData && (
        <div className="absolute inset-0 overflow-hidden">
          <div
            className={`w-40 h-40 rounded-full border-[12px] ${npsBgColor(score)} transition-all duration-1000`}
            style={{
              clipPath: "inset(50% 0 0 0)",
              transform: `rotate(${rotation - 180}deg)`,
              transformOrigin: "center center",
              opacity: 0.8,
            }}
          />
        </div>
      )}
      {/* Score number */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
        <span className={`text-3xl font-bold ${hasData ? npsColor(score) : "text-muted-foreground"}`}>{hasData ? Math.round(score) : "--"}</span>
      </div>
    </div>
  )
}

// ==========================================
// New Campaign Modal
// ==========================================

function NewCampaignModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState("")
  const createCampaign = useCreateNpsCampaign()

  if (!open) return null

  const handleSubmit = async () => {
    if (!name.trim()) return
    await createCampaign.mutateAsync({ name: name.trim() })
    setName("")
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Nova Campanha NPS</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Nome da Campanha</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex: NPS Q1 2026"
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || createCampaign.isPending}>
            {createCampaign.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Criar Campanha
          </Button>
        </div>
      </div>
    </div>
  )
}

// ==========================================
// Main Page
// ==========================================

export default function CustomerSuccessPage() {
  const { data: dashboard, isLoading: dashLoading } = useNpsDashboard()
  const { data: campaignsData, isLoading: campaignsLoading } = useNpsCampaigns()
  const { data: surveysData } = useNpsSurveys({ status: "responded" })
  const { data: plansData } = useActionPlans({ status: "open" })
  const sendCampaign = useSendNpsCampaign()
  const [showNewCampaign, setShowNewCampaign] = useState(false)

  const campaigns = campaignsData?.items || []
  const surveys = surveysData?.items || []
  const plans = plansData?.items || []

  const respondedSurveys = surveys
    .filter((s) => s.score != null)
    .sort((a, b) => new Date(b.responded_at || b.created_at).getTime() - new Date(a.responded_at || a.created_at).getTime())
    .slice(0, 10)

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <HeartHandshake className="w-7 h-7 text-primary" />
            Customer Success
          </h1>
          <p className="text-muted-foreground mt-1">NPS, satisfacao e planos de acao</p>
        </div>
        <Button onClick={() => setShowNewCampaign(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Campanha NPS
        </Button>
      </div>

      {/* KPI Cards */}
      {dashLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : dashboard ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* NPS Score */}
          <Card className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <BarChart3 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">NPS Score</p>
                <p className={`text-sm font-medium ${dashboard.total_responded > 0 ? npsColor(dashboard.nps_score) : "text-muted-foreground"}`}>{dashboard.total_responded > 0 ? npsLabel(dashboard.nps_score) : "Sem dados"}</p>
              </div>
            </div>
            <NpsGauge score={dashboard.nps_score} hasData={dashboard.total_responded > 0} />
            <div className="flex justify-between mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-emerald-500" />
                {dashboard.promoters} Prom.
              </span>
              <span className="flex items-center gap-1">
                <Minus className="w-3 h-3 text-amber-500" />
                {dashboard.passives} Neut.
              </span>
              <span className="flex items-center gap-1">
                <TrendingDown className="w-3 h-3 text-red-500" />
                {dashboard.detractors} Det.
              </span>
            </div>
          </Card>

          {/* Taxa de Resposta */}
          <Card className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Send className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Taxa de Resposta</p>
                <p className="text-2xl font-bold">{dashboard.response_rate.toFixed(1)}%</p>
              </div>
            </div>
            <div className="mt-3">
              <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-700"
                  style={{ width: `${Math.min(100, dashboard.response_rate)}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {dashboard.total_responded} de {dashboard.total_sent} enviados
              </p>
            </div>
          </Card>

          {/* Detratores Abertos */}
          <Card className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${dashboard.open_action_plans > 0 ? "bg-red-500/10" : "bg-green-500/10"}`}>
                <AlertTriangle className={`w-5 h-5 ${dashboard.open_action_plans > 0 ? "text-red-500" : "text-green-500"}`} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Planos de Acao Abertos</p>
                <p className={`text-2xl font-bold ${dashboard.open_action_plans > 0 ? "text-red-500" : "text-green-500"}`}>
                  {dashboard.open_action_plans}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              {dashboard.detractors} detratores no total ({dashboard.detractor_pct.toFixed(0)}%)
            </p>
          </Card>

          {/* Clientes Ativos */}
          <Card className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-emerald-500/10 rounded-lg">
                <Building2 className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Clientes Ativos</p>
                <p className="text-2xl font-bold">{dashboard.active_clients}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              {dashboard.total_clients} clientes no total
            </p>
          </Card>
        </div>
      ) : null}

      {/* Campanhas Recentes */}
      <Card>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Send className="w-5 h-5 text-primary" />
            Campanhas Recentes
          </h2>
        </div>
        <div className="overflow-x-auto">
          {campaignsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : campaigns.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Send className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>Nenhuma campanha criada ainda.</p>
              <p className="text-sm mt-1">Clique em &quot;Nova Campanha NPS&quot; para comecar.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="px-5 py-3 font-medium text-muted-foreground">Nome</th>
                  <th className="px-5 py-3 font-medium text-muted-foreground">Tipo</th>
                  <th className="px-5 py-3 font-medium text-muted-foreground">Status</th>
                  <th className="px-5 py-3 font-medium text-muted-foreground">Respondidos</th>
                  <th className="px-5 py-3 font-medium text-muted-foreground">Criada em</th>
                  <th className="px-5 py-3 font-medium text-muted-foreground">Acoes</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.slice(0, 10).map((c) => {
                  const st = CAMPAIGN_STATUS[c.status] || CAMPAIGN_STATUS.draft
                  return (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-muted/50 transition-colors">
                      <td className="px-5 py-3 font-medium">{c.name}</td>
                      <td className="px-5 py-3 text-muted-foreground">
                        {c.campaign_type === "nps_relational" ? "NPS Relacional" : "CSAT Transacional"}
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${st.color}`}>{st.label}</span>
                      </td>
                      <td className="px-5 py-3">
                        <span className="font-medium">{c.responded_count}</span>
                        <span className="text-muted-foreground">/{c.total_surveys}</span>
                      </td>
                      <td className="px-5 py-3 text-muted-foreground">{formatDate(c.created_at)}</td>
                      <td className="px-5 py-3">
                        {c.status === "draft" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => sendCampaign.mutate(c.id)}
                            disabled={sendCampaign.isPending}
                          >
                            <Send className="w-3.5 h-3.5 mr-1" />
                            Enviar
                          </Button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* Grid: Respostas + Planos de Acao */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Respostas Recentes */}
        <Card>
          <div className="p-5 border-b">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Respostas Recentes
            </h2>
          </div>
          <div className="divide-y max-h-[400px] overflow-y-auto">
            {respondedSurveys.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>Nenhuma resposta ainda.</p>
              </div>
            ) : (
              respondedSurveys.map((s) => (
                <div key={s.id} className="px-5 py-3 flex items-center gap-3 hover:bg-muted/50 transition-colors">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${scoreColor(s.score)}`}>
                    {s.score}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{s.company_name || "---"}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {s.contact_name || "Sem contato"} &bull; {scoreCategory(s.score)}
                    </p>
                  </div>
                  <div className="text-xs text-muted-foreground text-right shrink-0">
                    {s.responded_at ? formatDate(s.responded_at) : "---"}
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Planos de Acao */}
        <Card>
          <div className="p-5 border-b">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-primary" />
              Planos de Acao Abertos
            </h2>
          </div>
          <div className="divide-y max-h-[400px] overflow-y-auto">
            {plans.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>Nenhum plano de acao aberto.</p>
              </div>
            ) : (
              plans.map((p) => {
                const sev = SEVERITY_CONFIG[p.severity] || SEVERITY_CONFIG.medium
                const st = AP_STATUS[p.status] || AP_STATUS.open
                const slaDate = p.sla_close ? new Date(p.sla_close) : null
                const daysLeft = slaDate ? Math.ceil((slaDate.getTime() - Date.now()) / 86400000) : null
                return (
                  <div key={p.id} className="px-5 py-3 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium truncate">{p.company_name || "---"}</p>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${sev.color}`}>{sev.label}</span>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${st.color}`}>{st.label}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>Score: <strong className={scoreColor(p.survey_score) ? "text-foreground" : ""}>{p.survey_score ?? "---"}</strong></span>
                      {daysLeft !== null && (
                        <span className={daysLeft < 0 ? "text-red-500 font-semibold" : daysLeft <= 3 ? "text-amber-500" : ""}>
                          SLA: {daysLeft < 0 ? `${Math.abs(daysLeft)}d atrasado` : `${daysLeft}d restantes`}
                        </span>
                      )}
                      {p.root_cause && <span className="truncate">Causa: {p.root_cause}</span>}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </Card>
      </div>

      {/* NPS Distribution Bar */}
      {dashboard && dashboard.total_responded > 0 && (
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-3">Distribuicao NPS</h3>
          <div className="flex h-6 rounded-full overflow-hidden">
            {dashboard.promoter_pct > 0 && (
              <div
                className="bg-emerald-500 flex items-center justify-center text-xs text-white font-medium transition-all duration-700"
                style={{ width: `${dashboard.promoter_pct}%` }}
                title={`Promotores: ${dashboard.promoter_pct.toFixed(1)}%`}
              >
                {dashboard.promoter_pct >= 10 ? `${dashboard.promoter_pct.toFixed(0)}%` : ""}
              </div>
            )}
            {dashboard.passive_pct > 0 && (
              <div
                className="bg-amber-500 flex items-center justify-center text-xs text-white font-medium transition-all duration-700"
                style={{ width: `${dashboard.passive_pct}%` }}
                title={`Neutros: ${dashboard.passive_pct.toFixed(1)}%`}
              >
                {dashboard.passive_pct >= 10 ? `${dashboard.passive_pct.toFixed(0)}%` : ""}
              </div>
            )}
            {dashboard.detractor_pct > 0 && (
              <div
                className="bg-red-500 flex items-center justify-center text-xs text-white font-medium transition-all duration-700"
                style={{ width: `${dashboard.detractor_pct}%` }}
                title={`Detratores: ${dashboard.detractor_pct.toFixed(1)}%`}
              >
                {dashboard.detractor_pct >= 10 ? `${dashboard.detractor_pct.toFixed(0)}%` : ""}
              </div>
            )}
          </div>
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> Promotores ({dashboard.promoters})</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> Neutros ({dashboard.passives})</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Detratores ({dashboard.detractors})</span>
          </div>
        </Card>
      )}

      {/* Modal */}
      <NewCampaignModal open={showNewCampaign} onClose={() => setShowNewCampaign(false)} />
    </div>
  )
}
